import { ApolloError } from '@apollo/client';
import { Control, FieldErrors } from 'react-hook-form';
import {
  BillingType,
  ClientType,
  InsurerType,
  PartnershipType,
} from '../../types';

/**
 * Base filter props that work with any form type
 * Uses `any` for flexibility across different form structures
 */
export interface BaseFilterProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
}

export interface ClientFilterProps extends BaseFilterProps {
  name?: string;
  clients: ClientType[];
  hasError?: boolean;
  variant?: 'standard' | 'outlined';
  sx?: Record<string, unknown>;
  label?: string;
  loading?: boolean;
}

export interface InsurerFilterProps extends BaseFilterProps {
  name?: string;
  insurers: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  hasError?: boolean;
  variant?: 'standard' | 'outlined';
  sx?: Record<string, unknown>;
  label?: string;
  loading?: boolean;
}

export interface PartnershipFilterProps extends BaseFilterProps {
  name?: string;
  partnerships: PartnershipType[];
  hasError?: boolean;
  variant?: 'standard' | 'outlined';
  sx?: Record<string, unknown>;
  label?: string;
  loading?: boolean;
}

export interface HoldReasonFilterProps extends BaseFilterProps {
  name?: string;
  holdTypes: string[];
  holdTypesError?: ApolloError;
  disabled?: boolean;
}

export interface SubmissionStatusFilterProps extends BaseFilterProps {
  name?: string;
  disabled?: boolean;
}

export interface DateRangeFilterProps extends BaseFilterProps {
  name: string;
  labelFrom: string;
  labelTo: string;
  tooltipText: string;
}

export interface SourceFilterProps extends BaseFilterProps {
  name?: string;
  onSourceChange?: (source: string | null) => void;
}

export interface CSVInputFilterProps extends BaseFilterProps {
  name: string;
  label: string;
  disabled?: boolean;
  showClearButton?: boolean;
}

export interface BillingTypeFilterProps extends BaseFilterProps {
  billingTypes: BillingType[];
  variant?: 'standard' | 'outlined';
  label?: string;
  loading?: boolean;
  disabled?: boolean;
}

export type CiStatusFilterProps = BaseFilterProps;

export type ContractStatusFilterProps = BaseFilterProps;

export enum StatusFilterLabelText {
  HOLD_REASON = 'Hold Reason',
  SOURCE_ID = 'Source ID',
  USER_ID = 'User ID',
  CLIENT = 'Client',
  INSURER = 'Insurer',
  PARTNERSHIP = 'Partnership',
  BILL_TYPE = 'Billing Type',
  SOURCE = 'Source',
  BILL_STATUS = 'Bill Status',
  SUBMISSION_STATUS = 'Submission Status',
  NETSUITE_ID = 'NetSuite ID',
  CREDIT_MEMO_ID = 'Credit Memo ID',
  PAYMENT_TYPES = 'Payment Types',
  BILL_ID = 'Bill ID',
  SOURCE_FROM = 'Source From',
  SOURCE_TO = 'Source To',
  DOS_FROM = 'DOS From',
  DOS_TO = 'DOS To',
  CI_STATUS = 'Client Insurer Status',
  CONTRACT_STATUS = 'Contract Status',
}
